<template>

    <ui-view class="view-current-cohorts" v-spinner="loading" ref="contentElement">

        <ui-heading title="Huidige partijen" icon="magicon-inbox">
            <ui-dropdown :options="quantityUnitOptions" v-model="quantityUnit"/>
            <ui-dropdown empty="Alle gebieden" :options="parcelAreaOptions" v-model="areaFilter"/>
            <ui-searchbar v-model="search"/>
            <ui-button color="highlight-1" @click="exportExcel()">Exporteren</ui-button>
        </ui-heading>

        <ui-panel
            class="lot"
            v-for="parcel in $safe('parcels.items')"
            :key="parcel.id"
            v-if="$safe(parcel, 'sections.items').length">

            <div class="parcel-header-wrapper">
                <h2>Perceel {{ parcel.parcelLocation.name }} {{ parcel.parcelLocation.number }}</h2>
                <ui-button v-if="parcelPredictionsByParcelId.get(parcel.id)" color="highlight-1" @click="showPredictionModal(parcelPredictionsByParcelId.get(parcel.id))">Voorspellingen</ui-button>
            </div>

            <span class="subtitle" v-if="parcel.alias">(Alias: {{ parcel.alias }})</span>

            <div class="sections-holder">
                <div class="section-wrapper" v-for="section in $safe(parcel, 'sections.items')" :key="section.id">
                    <ui-panel class="section" :padding="false">
                        <div class="section-title">
                            <ui-link
                                class="section-link"
                                :disabled="!$can('ROUTE_SECTIONS')"
                                v-tooltip="$can('ROUTE_SECTIONS') ? 'Vak detail' : ''"
                                :to="{ name: 'section-detail', params: { id: section.id }}">Vak {{ section.name }}</ui-link>
                            <span class="cohort-link"><strong>{{ $safe(section, 'activeCohort.startDate') | date('LL') | empty }}</strong></span>
                        </div>
                        <div class="section-content">
                            <table>
                                <tr>
                                    <th style="width: 50%">Gezaaid</th>
                                    <th style="width: 50%">Gevist</th>
                                </tr>
                                <tr>
                                    <template v-if="quantityUnit === 'BRUTO'">
                                        <td>{{ $safe(section, 'activeCohort.sowingTotal.brutoWeight') | number | empty }}
                                            <span class="unit">mTon</span></td>
                                        <td>{{ $safe(section, 'activeCohort.harvestTotal.brutoWeight') | number | empty }}
                                            <span class="unit">mTon</span></td>
                                    </template>

                                    <template v-else-if="quantityUnit === 'NETTO'">
                                        <td>{{ $safe(section, 'activeCohort.sowingTotal.nettoWeight') | number | empty }}
                                            <span class="unit">mTon</span></td>
                                        <td>{{ $safe(section, 'activeCohort.harvestTotal.nettoWeight') | number | empty }}
                                            <span class="unit">mTon</span></td>
                                    </template>
                                </tr>
                                <tr>
                                    <template v-if="quantityUnit === 'BRUTO'">
                                        <td>{{ $safe(section, 'activeCohort.sowingDensity.bruto') | number | empty }}
                                            <span class="unit">mTon/ha</span></td>
                                        <td></td>
                                    </template>
                                    <template v-else-if="quantityUnit === 'NETTO'">
                                        <td>{{ $safe(section, 'activeCohort.sowingDensity.netto') | number | empty }}
                                            <span class="unit">mTon/ha</span></td>
                                        <td></td>
                                    </template>
                                </tr>
                                <tr>
                                    <td>{{ $safe(section, 'activeCohort.sowingTotal.bustal') | number | empty }}
                                        <span class="unit">Bustal</span></td>
                                    <td>{{ $safe(section, 'activeCohort.harvestTotal.bustal') | number | empty }}
                                        <span class="unit">Bustal</span></td>
                                </tr>
                            </table>
                        </div>
                    </ui-panel>
                </div>
            </div>
        </ui-panel>

        <div class="text-placeholder" v-else-if="!loading && !fetchingParcels">
            Er zijn momenteel geen partijen om hier weer te geven.
        </div>

        <div
            v-if="hasMoreParcels || (parcels == null && loading)"
            style="background:none;"
            class="page-loader"
            v-spinner="true"></div>

        <span v-if="parcels && parcels.totalCount === 0" class="placeholder-full">
            Er zijn geen percelen gevonden
        </span>

    </ui-view>

</template>

<script>

import Vue from 'vue';
import { clone } from 'es-toolkit';
import Component from 'vue-class-component';

import pagingMixin from 'js-core/js/mixins/paging';
import createQuery from 'js-core/js/apollo/queries/createQuery';

import QuantityUnit from 'common/enums/QuantityUnit';

import AllParcelsQuery from 'gql/queries/CurrentCohorts/AllParcels.gql';
import ParcelPredictionsQuery from 'gql/queries/CurrentCohorts/ParcelPredictions.gql';
import AllParcelLocationAreas from 'gql/queries/CurrentCohorts/AllParcelLocationAreas.gql';

import navigationMixin from 'app/mixins/navigation';
import ParcelPredictionModal from 'app/components/modals/ParcelPredictionModal.vue';

Component.registerHooks(['beforeRouteLeave']);

const SAVED_SCROLL_POSITION_KEY = 'CurrentCohortsSavedScrollPosition';
const FETCH_SCROLL_THRESHOLD = 200;

@Component({

    name: 'CurrentCohorts',
    mixins: [navigationMixin, pagingMixin],
    apollo: {

        parcels: createQuery(AllParcelsQuery, {

            variables(){

                return {
                    sortField: 'parcelLocation',
                    sortDirection: 'ASC',
                    search: this.search,
                    offset: this.paging.offset,
                    limit: this.paging.limit,
                    filter: this.areaFilter ? {
                        parcelLocationArea: [this.areaFilter]
                    } : null
                };
            }
        }),

        parcelAreas: createQuery(AllParcelLocationAreas),

        parcelPredictions: createQuery(ParcelPredictionsQuery)
    }
})
export default class CurrentCohorts extends Vue {

    loading = 0;
    parcels = null;
    fetchingParcels = false;
    parcelScroller = null;

    areaFilter = this.$persist('areaFilter');
    search = this.$persist('search');

    parcelPredictions = null;

    quantityUnit = this.$persist('quantityUnit', QuantityUnit.BRUTO);

    get hasMoreParcels(){

        if(this.parcels == null){
            return false;
        }

        return this.parcels.items.length < this.parcels.totalCount;
    }

    breadcrumb(){

        return 'Huidige partijen';
    }

    mounted(){

        const savedScrollPosition = window.sessionStorage.getItem(SAVED_SCROLL_POSITION_KEY);
        const wrapper = document.querySelector('.layout-wrapper');

        if(savedScrollPosition){
            wrapper.scrollTop = savedScrollPosition;
        }

        this.parcelScroller = document.getElementsByClassName('layout-wrapper')[0];
        this.parcelScroller.addEventListener('scroll', this.handleScroll);
    }

    showPredictionModal(forecast) {

        this.$modal(ParcelPredictionModal,{
            forecast
        });
    }

    unmounted(){

        this.parcelScroller.removeEventListener('scroll', this.handleScroll);
    }

    beforeRouteLeave(to, from, next){

        const wrapper = document.querySelector('.layout-wrapper');
        window.sessionStorage.setItem(SAVED_SCROLL_POSITION_KEY, wrapper.scrollTop);

        next();
    }

    exportExcel(){

        this.navigateExport('excel/overview');
    }

    get parcelAreaOptions(){

        return (this.parcelAreas || []).map(area => ({
            label: area,
            value: area
        }));
    }

    get quantityUnitOptions(){

        return QuantityUnit.options();
    }

    get parcelPredictionsByParcelId() {

        let parcelPredictionsByParcelId = new Map();

        if(!this.parcelPredictions) {
            return parcelPredictionsByParcelId;
        }

        for(let parcelPrediction of this.parcelPredictions) {
            parcelPredictionsByParcelId.set(parcelPrediction.id, parcelPrediction.forecast);
        }

        return parcelPredictionsByParcelId;
    }

    handleScroll(){

        if(!this.hasMoreParcels || this.fetchingParcels){
            return;
        }

        if((parseInt(this.$refs.contentElement?.$el?.getBoundingClientRect()?.bottom?.toString() || '0') - FETCH_SCROLL_THRESHOLD) < window.innerHeight){

            this.fetchNextParcels();
        }
    }

    async fetchNextParcels(){

        this.fetchingParcels = true;

        try {

            const offset = this.parcels.items.length;

            await this.$apollo.queries.parcels.fetchMore({

                variables: {
                    offset,
                    limit: this.paging.limit
                },

                updateQuery: (previousResult, { fetchMoreResult }) => {

                    if(!previousResult || !fetchMoreResult){
                        return previousResult;
                    }

                    const newResult = clone(fetchMoreResult);

                    newResult.allParcels.items = [
                        ...previousResult.allParcels.items,
                        ...fetchMoreResult.allParcels.items
                    ];

                    return newResult;
                }
            });
        }
        catch(e){

            this.logger.error(e);
            this.$toast.error('De partijen konden niet worden ingeladen.');
        }

        this.fetchingParcels = false;
    }
}

</script>